import { memo, useMemo } from 'react';
import { Match, League } from '@/lib/types';
import { MatchCard } from './MatchCard';
import { LeagueBadge } from './LeagueBadge';

interface LeagueSectionProps {
  league: League;
  matches: Match[];
}

// Memoized league header with optimized image - compact for mobile
const LeagueHeader = memo(function LeagueHeader({ league }: { league: League }) {
  return (
    <header className="flex items-center gap-2 sm:gap-3 px-0.5 sm:px-1 py-1.5 sm:py-2">
      <LeagueBadge 
        logoUrl={league.logo_url} 
        leagueName={league.name}
        size="sm"
        className="transition-transform duration-200 group-hover:scale-105"
      />
      <div>
        <h2 id={`league-${league.id}`} className="font-semibold text-xs sm:text-sm text-foreground leading-tight">
          {league.name}
        </h2>
        {league.country && (
          <p className="text-[9px] sm:text-xs text-muted-foreground mt-0.5">{league.country}</p>
        )}
      </div>
    </header>
  );
});

function LeagueSectionComponent({ league, matches }: LeagueSectionProps) {
  if (matches.length === 0) return null;

  return (
    <section aria-labelledby={`league-${league.id}`} className="space-y-1 sm:space-y-2 animate-fade-in">
      <LeagueHeader league={league} />

      <div className="space-y-1 sm:space-y-1.5" role="list" aria-label={`Matches in ${league.name}`}>
        {matches.map((match, index) => (
          <article 
            key={match.id} 
            role="listitem"
            className="animate-fade-in"
            style={{ animationDelay: `${index * 30}ms` }}
          >
            <MatchCard match={match} />
          </article>
        ))}
      </div>
    </section>
  );
}

// Custom comparison - only re-render when matches change
export const LeagueSection = memo(LeagueSectionComponent, (prevProps, nextProps) => {
  if (prevProps.league.id !== nextProps.league.id) return false;
  if (prevProps.league.name !== nextProps.league.name) return false;
  if (prevProps.matches.length !== nextProps.matches.length) return false;
  
  // Check if any match data changed
  for (let i = 0; i < prevProps.matches.length; i++) {
    const prev = prevProps.matches[i];
    const next = nextProps.matches[i];
    if (
      prev.id !== next.id ||
      prev.status !== next.status ||
      prev.home_score !== next.home_score ||
      prev.away_score !== next.away_score
    ) {
      return false;
    }
  }
  
  return true;
});
